import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { CostStringSchema } from './items.js';
import { pagedQuery, pagedResponse } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema } from './primitives.js';
const c = initContract();
/**
 * Stock In / Out vouchers (INV-08) — stock arriving or leaving for a reason
 * no other document covers, always saying WHY. Many lines, a COST per line
 * (BR-INV-08-17 — never the selling price), and an optional approval gate.
 *
 * Lifecycle: Draft (freely editable, moves nothing — BR-INV-08-18) → Posted
 * (one Adjustment± movement per line carrying the cost, BR-INV-08-09/-11;
 * immutable, corrected by an opposite voucher — BR-INV-08-13) | Cancelled.
 * SI-####/SO-#### advance independently and ONLY at posting (BR-INV-08-02);
 * a draft shows the next number as a preview.
 *
 * Approval (AP-6): the company default is OFF; when required, pending →
 * approved | rejected by inventory.approve, and only Approved unblocks
 * posting — Rejected blocks DISTINCTLY (BR-INV-08-07/-08).
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const ADJUSTMENT_MESSAGES = {
    /** BR-INV-08-07 / ERR-INV-08-01. */
    approvalRequired: 'Approval required before posting.',
    /**
     * BR-INV-08-08 / ERR-INV-08-02 [Required addition] — rejected says so
     * distinctly. The prototype showed the pending sentence for both.
     */
    rejected: 'This voucher was rejected and cannot be posted. Return it to Draft and fix it first.',
    /** BR-INV-08-13. */
    postedLocked: 'This voucher is posted. Raise an opposite voucher to correct it.',
    /** BR-INV-08-07's third condition / ERR-INV-08-03 — every line at zero. */
    zeroTotal: 'Add at least one line with a quantity before posting.',
    /** Not a draft (double-post, cancel-after-post). */
    notADraft: 'Only a draft voucher can change. This one has already moved on.',
    /** GEN-59: the cancelling act carries its reason — distinct from the voucher's own stock reason. */
    cancelReasonRequired: 'A voucher can only be cancelled with a reason, which is stored on the voucher and written to the audit log.',
    /** Approving without the gate switched on. */
    approvalNotRequired: 'This voucher does not require approval — it can be posted directly.',
    /** BR-FIN-04-02's shape: nobody approves their own voucher. */
    selfApproval: 'A voucher cannot be approved by the person who raised it.',
    /**
     * MD-03 field 7 / R7: a kit with auto-deduct ON is assembled at the moment
     * of sale and is never stocked as itself, so there is no quantity of it for
     * a voucher to move. Naming its parts is not a workaround — it is the only
     * statement that is true, and it is the one the §12 entry can value, since
     * a voucher posts its own typed cost and the parts move at THEIRS.
     */
    kitNotStocked: (name) => `${name} is assembled when it is sold and is never stocked on its own. Put its parts on the voucher instead.`,
};
/**
 * D46 — the kit-not-stocked adjustment refusal, structured. `name` is the
 * item's display name. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const ADJUSTMENT_REFUSALS = {
    kitNotStocked: (name) => ({
        message: ADJUSTMENT_MESSAGES.kitNotStocked(name),
        key: 'adj.errKitNotStocked',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const StockInReasonSchema = z.enum([
    'opening_balance',
    'found_stock',
    'production_output',
    'customer_return_non_invoice',
    'gift_received',
    'other',
]);
export const StockOutReasonSchema = z.enum([
    'damaged_expired',
    'internal_use',
    'samples_marketing',
    'production_usage',
    'donation',
    'shrinkage_loss',
    /** PRJ-01: issued to a project — the voucher names it in `projectId`. */
    'project_issue',
    'other',
]);
export const AdjustmentReasonSchema = z.union([StockInReasonSchema, StockOutReasonSchema]);
/**
 * The two direction sets, as data rather than as two hand-kept copies.
 *
 * `stock-adjustments.service.ts` had them as module-private constants and
 * ADM-03's stock-balance import needs the same question answered ("does this
 * reason raise stock or lower it?"), so they are published from the one place
 * the enums are declared. A second copy is a second answer, and the one that
 * gets a new reason added to it would not be the one in use.
 */
export const STOCK_IN_REASONS = new Set(StockInReasonSchema.options);
export const STOCK_OUT_REASONS = new Set(StockOutReasonSchema.options);
/**
 * Every reason once, in-reasons then out-reasons, `other` not repeated.
 *
 * For a picker that must offer BOTH directions — ADM-03's stock-balance
 * import, where one file can raise some quantities and lower others and the
 * person picks one reason for the file. INV-08's own form picks a direction
 * first and so uses the two lists separately.
 */
export const ADJUSTMENT_REASONS = [
    ...StockInReasonSchema.options,
    ...StockOutReasonSchema.options.filter((reason) => !STOCK_IN_REASONS.has(reason)),
];
export const AdjustmentDirectionSchema = z.enum(['in', 'out']);
export const AdjustmentApprovalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export const StockAdjustmentLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    unitSymbol: z.string(),
    /**
     * `N-4` — the decimals the unit allows. A voucher line moves the item's
     * STOCK unit (the input takes an item, a quantity and a cost; there is no
     * unit to pick), so this is `units.precision` for `items.stock_unit_id`.
     * A POSTED voucher is a record, and a record that prints "40" where the
     * shelf is measured in thousandths is claiming a precision it does not
     * have.
     */
    unitPrecision: UnitPrecisionSchema,
    qty: z.string(),
    unitCost: z.string(),
    lineValue: z.string(),
    /** BR-MD-03-17: the units this line moves, one serial each. */
    serialNos: z.string().array().nullable(),
    /** The lot this line moves. One lot per line. */
    batchNo: z.string().nullable(),
})
    .strict();
export const StockAdjustmentSchema = z
    .object({
    id: z.uuid(),
    /** SI-0007 / SO-0004 once posted; the PREVIEW of the next number while draft. */
    code: z.string(),
    adjustmentNo: z.number().int().nullable(),
    direction: AdjustmentDirectionSchema,
    adjustmentDate: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    binId: z.uuid().nullable(),
    binCode: z.string().nullable(),
    reason: AdjustmentReasonSchema,
    requestedBy: z.string(),
    requireApproval: z.boolean(),
    approvalStatus: AdjustmentApprovalStatusSchema.nullable(),
    approvedByName: z.string().nullable(),
    approvedAt: z.string().nullable(),
    status: z.enum(['draft', 'posted', 'cancelled']),
    postedAt: z.string().nullable(),
    /** GEN-59: why the voucher was cancelled — null until it is, and on rows cancelled before the reason existed. NOT the stock `reason` above. */
    cancelReason: z.string().nullable(),
    totalQty: z.string(),
    totalValue: z.string(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    /**
     * PRJ-01 — the project a Stock OUT issues material to; null on every
     * other voucher. Its posted cost value folds into the project's derived
     * Spent (BR-PRJ-01-04). The code and name ride along so the register can
     * print "PRJ-0007 · Tower 14" without a second read.
     */
    projectId: z.uuid().nullable(),
    projectCode: z.string().nullable(),
    projectName: z.string().nullable(),
    lines: StockAdjustmentLineSchema.array(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
const AdjustmentLineInputSchema = z
    .object({
    itemId: z.uuid(),
    /** A positive magnitude; the head's direction signs it (BR-INV-06-07's shape). */
    qty: QtyStringSchema,
    /** Absent = the server fills the location's current average cost (falling back to the item's cost price). */
    unitCost: CostStringSchema.optional(),
    /**
     * BR-MD-03-17, the same shape every other document line uses: one serial
     * per unit for a serial-tracked item, or the lot for a batch-tracked one.
     * A draft may be saved short of them — INV-08 §5.4's grid is freely
     * editable (BR-INV-08-18); POSTING is where they are counted.
     */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
})
    .strict();
const StockAdjustmentHeadSchema = z
    .object({
    direction: AdjustmentDirectionSchema,
    adjustmentDate: z.iso.date(),
    warehouseId: z.uuid(),
    binId: z.uuid().nullable().optional(),
    reason: AdjustmentReasonSchema,
    requestedBy: z.string().trim().min(1).max(120),
    requireApproval: z.boolean().optional(),
    notes: z.string().max(2000).nullable().optional(),
    /** PRJ-01: the project this Stock OUT issues material to. OUT only. */
    projectId: z.uuid().nullable().optional(),
    lines: AdjustmentLineInputSchema.array().min(1).max(200),
})
    .strict();
/**
 * PRJ-01's two shape rules, decided HERE so the server owes no sentence for
 * them (the DB CHECK is the floor beneath):
 *   - only a Stock OUT names a project — stock does not arrive FROM a project
 *     through this door;
 *   - the `project_issue` reason is meaningless without the project it issues
 *     to.
 * On the PATCH body both fields may be absent, so each rule fires only when
 * the pair it judges is present; the service resolves the rest against the
 * stored row.
 */
function projectIssueRules(value, ctx) {
    if (value.direction === 'in' && value.projectId != null) {
        ctx.addIssue({
            code: 'custom',
            path: ['projectId'],
            message: 'Only a Stock Out voucher can be issued to a project.',
        });
    }
    if (value.reason === 'project_issue' && value.projectId === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['projectId'],
            message: 'A project issue names the project it is issued to.',
        });
    }
}
export const CreateStockAdjustmentRequestSchema = StockAdjustmentHeadSchema.superRefine((value, ctx) => projectIssueRules({ ...value, projectId: value.projectId ?? null }, ctx));
export const UpdateStockAdjustmentRequestSchema = StockAdjustmentHeadSchema.partial().superRefine(projectIssueRules);
const adjustmentParams = z.object({ adjustmentId: z.uuid() }).strict();
export const stockAdjustmentsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/stock-adjustments',
        query: pagedQuery({
            direction: AdjustmentDirectionSchema.optional(),
            status: z.enum(['draft', 'posted', 'cancelled']).optional(),
            warehouseId: z.uuid().optional(),
            /** PRJ-01: the vouchers issued to one project. */
            projectId: z.uuid().optional(),
        }),
        responses: {
            200: pagedResponse('adjustments', StockAdjustmentSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Stock In/Out vouchers (inventory.view), newest first, PAGED (NFR-42) — all four filters in SQL.',
    },
    get: {
        method: 'GET',
        path: '/api/stock-adjustments/:adjustmentId',
        pathParams: adjustmentParams,
        responses: {
            200: StockAdjustmentSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One voucher with its lines (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/stock-adjustments',
        body: CreateStockAdjustmentRequestSchema,
        responses: {
            201: StockAdjustmentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft voucher (inventory.create). Direction drives the reason list (BR-INV-08-01/-03); costs default to the location average, never the selling price (BR-INV-08-17).',
    },
    update: {
        method: 'PATCH',
        path: '/api/stock-adjustments/:adjustmentId',
        pathParams: adjustmentParams,
        body: UpdateStockAdjustmentRequestSchema,
        responses: {
            200: StockAdjustmentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (inventory.edit; BR-INV-08-18 — everything is editable while draft; posted answers with the fixed sentence). Editing resets a pending approval decision.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/stock-adjustments/:adjustmentId',
        pathParams: adjustmentParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (inventory.delete). A posted voucher is never deleted (BR-INV-08-13).',
    },
    approve: {
        method: 'POST',
        path: '/api/stock-adjustments/:adjustmentId/approve',
        pathParams: adjustmentParams,
        body: z.object({ decision: z.enum(['approved', 'rejected']) }).strict(),
        responses: {
            200: StockAdjustmentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Approve or reject (inventory.approve — AP-6). Not by the requester; only Approved unblocks posting (BR-INV-08-08).',
    },
    post: {
        method: 'POST',
        path: '/api/stock-adjustments/:adjustmentId/post',
        pathParams: adjustmentParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: StockAdjustmentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post (inventory.approve): allocates SI-/SO-#### (BR-INV-08-02), writes one Adjustment± movement per line carrying the cost (BR-INV-08-09/-11), posts the reason-mapped INV-08 §12 entry, updates the balances. One transaction.',
    },
    cancel: {
        method: 'POST',
        path: '/api/stock-adjustments/:adjustmentId/cancel',
        pathParams: adjustmentParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, ADJUSTMENT_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: StockAdjustmentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Abandon a DRAFT (inventory.edit). Draft → Cancelled; a posted voucher cannot cancel. GEN-59: reason required, stored on the voucher and written to the audit row.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=stock-adjustments.js.map